import { Injectable, signal } from '@angular/core';
import { IdbStorageService, IdbStorage } from './idb-storage.service';
import { ToastService } from './toast.service';
import { game } from '../../game';

const DIRECTORY_KEY = 'directory:game';

@Injectable({ providedIn: 'root' })
export class FileSaveService {
  readonly supportsFileSystemAccess = 'showDirectoryPicker' in window;

  private _hasGameDirectory = signal(false);
  readonly hasGameDirectory = this._hasGameDirectory.asReadonly();

  private _gameDirectoryName = signal<string | null>(null);
  readonly gameDirectoryName = this._gameDirectoryName.asReadonly();

  private fileHandleDb: IdbStorage;

  constructor(private toastService: ToastService, idbStorageService: IdbStorageService) {
    this.fileHandleDb = idbStorageService.createStorage(game + '-file-handles');
    if (this.supportsFileSystemAccess) {
      this.fileHandleDb.idbGet<FileSystemDirectoryHandle>(DIRECTORY_KEY).then(handle => {
        if (handle) {
          this._hasGameDirectory.set(true);
          this._gameDirectoryName.set(handle.name);
        }
      });
    }
  }

  async selectGameDirectory(): Promise<boolean> {
    let handle: FileSystemDirectoryHandle;
    try {
      handle = await (window as any).showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'documents',
      });
    } catch (err: any) {
      if (err?.name === 'AbortError') return false;
      this.toastService.raise('error', 'Could not open folder picker.', 'Folder selection failed');
      return false;
    }

    await this.fileHandleDb.idbSet(DIRECTORY_KEY, handle);
    this._hasGameDirectory.set(true);
    this._gameDirectoryName.set(handle.name);
    this.toastService.raise('success', `Game folder set to "${handle.name}". You can now save filters to file.`);
    return true;
  }

  async save(text: string, filename: string): Promise<void> {
    let dirHandle = await this.fileHandleDb.idbGet<FileSystemDirectoryHandle>(DIRECTORY_KEY);

    if (!dirHandle) {
      const selected = await this.selectGameDirectory();
      if (!selected) return;
      dirHandle = await this.fileHandleDb.idbGet<FileSystemDirectoryHandle>(DIRECTORY_KEY);
      if (!dirHandle) return;
    }

    const permission = await this._ensureWritePermission(dirHandle);
    if (permission !== 'granted') {
      this._clearDirectory();
      const selected = await this.selectGameDirectory();
      if (!selected) return;
      dirHandle = await this.fileHandleDb.idbGet<FileSystemDirectoryHandle>(DIRECTORY_KEY);
      if (!dirHandle) return;
      if (await this._ensureWritePermission(dirHandle) !== 'granted') return;
    }

    let fileHandle: FileSystemFileHandle;
    try {
      fileHandle = await dirHandle.getFileHandle(filename, { create: true });
    } catch (err: any) {
      this.toastService.raise('error', err?.message ?? 'Could not create file in game folder.', 'Save failed');
      return;
    }

    try {
      const writable = await (fileHandle as any).createWritable();
      await writable.write(text);
      await writable.close();
    } catch (err: any) {
      this.toastService.raise('error', err?.message ?? 'Unknown error while writing file.', 'Save failed');
      return;
    }

    this.toastService.raise('success', `Saved to ${filename} in ${dirHandle.name}. Refresh in-game to see the changes.`);
  }

  private async _ensureWritePermission(dirHandle: FileSystemDirectoryHandle): Promise<PermissionState> {
    try {
      const existing = await (dirHandle as any).queryPermission({ mode: 'readwrite' });
      if (existing === 'granted') return 'granted';
      return await (dirHandle as any).requestPermission({ mode: 'readwrite' });
    } catch {
      return 'denied';
    }
  }

  private _clearDirectory(): void {
    this._hasGameDirectory.set(false);
    this._gameDirectoryName.set(null);
    this.fileHandleDb.idbDelete(DIRECTORY_KEY);
  }
}
